<template>
  <v-container fluid class="ncrm-workflow-details-page">
    <v-alert v-if="errorMessage" type="error" class="mb-4">
      {{ errorMessage }}
    </v-alert>
    <v-alert v-if="infoMessage" type="info" class="mb-4">
      {{ infoMessage }}
    </v-alert>
    <v-card v-if="!selectedWorkflow && !loadingExternalApi && !loadingWorkflows" class="pa-5 ncrm-workflow-details-page__empty">
      <div class="d-flex align-start ga-3">
        <v-icon color="warning" size="24">mdi-alert-circle-outline</v-icon>
        <div>
          <div class="text-subtitle-1 font-weight-semibold ncrm-text-strong">
            {{ $t("ncrm.workflows.detail.notFoundTitle") }}
          </div>
          <div class="text-body-2 text-medium-emphasis mt-1">
            {{ $t("ncrm.workflows.detail.notFoundBody") }}
          </div>
          <v-btn class="mt-4" color="primary" variant="tonal" @click="goBackToList">
            <v-icon start>mdi-arrow-left</v-icon>
            {{ $t("ncrm.workflows.actions.backToList") }}
          </v-btn>
        </div>
      </div>
    </v-card>

    <component
      :is="workflowDetailComponent"
      v-else-if="selectedWorkflow"
      :workflow="selectedWorkflow"
      :endpoints="externalApiEndpoints"
      :deliveries="selectedWorkflowDeliveries"
      :loading-deliveries="loadingDeliveries"
      :template-name="templateName(selectedWorkflow.templateKey)"
      :staging-records="stagingRecords"
      :loading-staging-records="loadingStaging"
      :loading-staging-import="loadingStagingImport"
      :loading-staging-dedup="loadingStagingDedup"
      :loading-staging-enrichment="loadingStagingEnrichment"
      :loading-staging-enrichment-sync="loadingStagingEnrichmentSync"
      :loading-staging-write="loadingStagingWrite"
      :loading-staging-lead-board="loadingStagingLeadBoard"
      :loading-mailjet-node="loadingMailjetNode"
      @run-node="handleRunMailjetNode"
      @import-csv="handleImportMailjetCsv"
      :auto-sync-settings="apiNodeSettings"
      :dedup-auto-run-settings="dedupNodeSettings"
      :enrichment-settings="enrichmentNodeSettings"
      :crm-write-settings="crmWriteNodeSettings"
      :lead-board-settings="leadBoardNodeSettings"
      :last-auto-sync-at="lastAutoSyncAt"
      :last-auto-dedup-at="lastAutoDedupAt"
      @back="goBackToList"
      @edit-workflow="openEditWorkflow"
      @edit-node="openNodeSettings"
      @refresh-deliveries="fetchDeliveriesForSelectedWorkflow"
      @open-external-api="openExternalApiSettings"
      @open-delivery="openDeliveryInExternalApi"
      @open-staging-record="openStagingRecord"
      @run-import="runStagingImport"
      @run-dedup="runWorkflowDedup"
      @open-file-import="openFileImportDialog"
      @open-ai-workspace="openAiWorkspacePrompts"
      @run-enrichment-batch="handleRunEnrichmentBatch"
      @run-enrichment-record="handleRunEnrichmentRecord"
      @sync-enrichment="handleSyncEnrichment"
      @write-staging-record="handleWorkflowWriteRecord"
      @write-approved-records="handleWriteApprovedRecords"
      @lead-board-add-record="handleLeadBoardAddRecord"
      @lead-board-add-batch="handleLeadBoardAddBatch"
    />
    <NcrmWorkflowEditorDrawer
      v-model="workflowDrawer"
      mode="edit"
      :workflow="editingWorkflow"
      :templates="workflowTemplates"
      @save="handleSaveWorkflow"
      @delete="handleDeleteWorkflow"
    />
    <NcrmWorkflowNodeSettingsDrawer
      v-model="nodeSettingsDrawer"
      :node="editingNode"
      :settings="editingNodeSettings"
      :endpoints="externalApiEndpoints"
      :loading-endpoints="loadingExternalApi"
      :workflow-instance-id="selectedWorkflow?.id || null"
      :ai-prompts="aiPrompts"
      :loading-ai-prompts="loadingAiPrompts"
      :ai-prompts-error="aiPromptsError"
      @save="handleSaveNodeSettings"
      @open-external-api="openExternalApiSettings"
      @open-ai-workspace="openAiWorkspacePrompts"
      @refresh-ai-prompts="fetchAiPrompts"
    />
    <NcrmWorkflowStagingDrawer
      v-model="stagingDrawer"
      :record="selectedStagingRecord"
      :loading="loadingStagingRecord"
      :saving="savingStagingRecord"
      @approve="handleStagingApprove"
      @reject="handleStagingReject"
      @run-dedup="handleStagingDedup"
      @recheck-dedup="handleStagingEnrichmentDedupRecheck"
      @write="handleStagingWrite"
      @remap="handleStagingRemap"
      @review-enrichment="handleStagingEnrichmentReview"
    />
    <NcrmWorkflowFileImportDialog
      v-if="selectedWorkflow"
      v-model="fileImportDialog"
      :workflow-instance-id="selectedWorkflow.id"
      @imported="handleFileImported"
    />
  </v-container>
</template>
<script>
import axios from "axios";
import NcrmContactNormalizationWorkflowDetail from "@/components/ncrm/workflows/NcrmContactNormalizationWorkflowDetail.vue";
import NcrmMailjetEngagementWorkflowDetail from "@/components/ncrm/workflows/NcrmMailjetEngagementWorkflowDetail.vue";
import NcrmWorkflowDetail from "@/components/ncrm/workflows/NcrmWorkflowDetail.vue";
import NcrmWorkflowEditorDrawer from "@/components/ncrm/workflows/NcrmWorkflowEditorDrawer.vue";
import NcrmWorkflowNodeSettingsDrawer from "@/components/ncrm/workflows/NcrmWorkflowNodeSettingsDrawer.vue";
import NcrmWorkflowStagingDrawer from "@/components/ncrm/workflows/NcrmWorkflowStagingDrawer.vue";
import NcrmWorkflowFileImportDialog from "@/components/ncrm/workflows/NcrmWorkflowFileImportDialog.vue";
import {
  fetchStagingRecords,
  fetchStagingRecord,
  importStagingFromEndpoint,
  updateStagingStatus,
  runDedupForStagingRecord,
  runDedupForWorkflow,
  runEnrichmentForStagingRecord,
  runEnrichmentForWorkflow,
  syncEnrichmentForWorkflow,
  reviewEnrichmentSuggestions,
  recheckEnrichmentDedupForStagingRecord,
  remapStagingFields,
} from "@/Utile/ncrmStagingApi.mjs";
import { workflowDetailsWriteLeadActions } from "./NcrmWorkflowDetailsWriteLeadActions.mjs";
import { workflowDetailsMailjetActions } from "./NcrmWorkflowDetailsMailjetActions.mjs";
import {
  deleteWorkflowInstanceApi,
  fetchWorkflowInstances,
  updateWorkflowInstanceApi,
  updateWorkflowNodeSettingsApi,
} from "@/Utile/ncrmWorkflowApi.mjs";
import {
  PUBLIC_EXTERNAL_FORM_SOURCE_SYSTEMS,
  WORKFLOW_NODE_KEYS,
  WORKFLOW_TEMPLATE_KEYS,
  createWorkflowTemplates,
  normalizeWorkflowInstance,
  resolveWorkflowApiEndpointId,
} from "@/Utile/ncrmWorkflows.mjs";
export default {
  name: "NcrmWorkflowDetailsPage",
  components: {
    NcrmContactNormalizationWorkflowDetail,
    NcrmMailjetEngagementWorkflowDetail,
    NcrmWorkflowDetail,
    NcrmWorkflowEditorDrawer,
    NcrmWorkflowNodeSettingsDrawer,
    NcrmWorkflowStagingDrawer,
    NcrmWorkflowFileImportDialog,
  },
  props: {
    workflowId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      workflows: [],
      workflowDrawer: false,
      editingWorkflow: null,
      nodeSettingsDrawer: false,
      editingNode: null,
      loadingWorkflows: false,
      loadingExternalApi: false,
      loadingDeliveries: false,
      errorMessage: "",
      externalApiEndpoints: [],
      selectedWorkflowDeliveries: [],
      stagingRecords: [],
      loadingStaging: false,
      loadingStagingImport: false,
      loadingStagingDedup: false,
      loadingStagingEnrichment: false,
      loadingStagingEnrichmentSync: false,
      loadingStagingWrite: false,
      loadingStagingLeadBoard: false,
      loadingMailjetNode: false,
      infoMessage: "",
      aiPrompts: [],
      loadingAiPrompts: false,
      aiPromptsError: "",
      stagingDrawer: false,
      selectedStagingRecord: null,
      loadingStagingRecord: false,
      savingStagingRecord: false,
      fileImportDialog: false,
      stagingRefreshTimer: null,
    };
  },
  computed: {
    workflowTemplates() {
      return createWorkflowTemplates(this.$t);
    },
    selectedWorkflow() {
      return this.workflows.find((workflow) => workflow.id === this.workflowId) || null;
    },
    workflowDetailComponent() {
      if (this.selectedWorkflow?.templateKey === WORKFLOW_TEMPLATE_KEYS.CONTACT_NORMALIZATION) {
        return "NcrmContactNormalizationWorkflowDetail";
      }
      if (this.selectedWorkflow?.templateKey === WORKFLOW_TEMPLATE_KEYS.MAILJET_ENGAGEMENT) {
        return "NcrmMailjetEngagementWorkflowDetail";
      }
      return "NcrmWorkflowDetail";
    },
    editingNodeSettings() {
      if (!this.selectedWorkflow || !this.editingNode?.key) return {};
      return this.selectedWorkflow.nodeSettings?.[this.editingNode.key] || {};
    },
    apiNodeSettings() {
      return this.selectedWorkflow?.nodeSettings?.[WORKFLOW_NODE_KEYS.API_IMPORT] || {};
    },
    dedupNodeSettings() {
      return this.selectedWorkflow?.nodeSettings?.[WORKFLOW_NODE_KEYS.DUPLICATE_CHECK] || {};
    },
    enrichmentNodeSettings() {
      return this.selectedWorkflow?.nodeSettings?.[WORKFLOW_NODE_KEYS.DATA_ENRICHMENT] || {};
    },
    crmWriteNodeSettings() {
      return this.selectedWorkflow?.nodeSettings?.[WORKFLOW_NODE_KEYS.CRM_WRITE] || {};
    },
    leadBoardNodeSettings() {
      return this.selectedWorkflow?.nodeSettings?.[WORKFLOW_NODE_KEYS.LEAD_BOARD_ADD] || {};
    },
    lastAutoSyncAt() {
      const v = this.selectedWorkflow?.lastApiSyncedAt;
      return v ? new Date(v) : null;
    },
    lastAutoDedupAt() {
      const v = this.selectedWorkflow?.lastDedupRunAt;
      return v ? new Date(v) : null;
    },
    autoSyncError() {
      return this.selectedWorkflow?.lastApiSyncError || null;
    },
    autoDedupError() {
      return this.selectedWorkflow?.lastDedupError || null;
    },
  },
  watch: {
    workflowId() {
      this.fetchDeliveriesForSelectedWorkflow();
      this.fetchStagingRecords();
    },
  },
  async mounted() {
    await Promise.all([
      this.fetchExternalApiWorkspace(),
      this.fetchAiPrompts(),
    ]);
    await this.loadWorkflows();
    await Promise.all([
      this.fetchDeliveriesForSelectedWorkflow(),
      this.fetchStagingRecords(),
    ]);
    this.stagingRefreshTimer = setInterval(() => {
      if (!this.selectedWorkflow?.id) return;
      this.fetchStagingRecords(); this.fetchDeliveriesForSelectedWorkflow();
      this.loadWorkflows();
    }, 60_000);
  },
  beforeUnmount() {
    if (this.stagingRefreshTimer) {
      clearInterval(this.stagingRefreshTimer);
      this.stagingRefreshTimer = null;
    }
  },
  methods: {
    templateName(templateKey) {
      return this.workflowTemplates.find((template) => template.key === templateKey)?.name || templateKey;
    },
    async loadWorkflows() {
      this.loadingWorkflows = true;
      try {
        const { data } = await fetchWorkflowInstances();
        const endpointId = this.externalApiEndpoints[0]?.id || null;
        this.workflows = (Array.isArray(data?.workflows) ? data.workflows : []).map((workflow) =>
          normalizeWorkflowInstance(workflow, {
            fallbackName: this.$t("ncrm.workflows.templates.enrichment.defaultInstanceName"),
            endpointId,
          })
        );
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.workflowsLoad");
        this.workflows = [];
      } finally {
        this.loadingWorkflows = false;
      }
    },
    persistWorkflowLocally(workflow) {
      this.workflows = this.workflows.map((item) => (item.id === workflow.id ? workflow : item));
    },
    async fetchExternalApiWorkspace() {
      this.loadingExternalApi = true;
      this.errorMessage = "";
      try {
        const { data } = await axios.get("/ncrm/integrations/external-api/workspace");
        this.externalApiEndpoints = Array.isArray(data?.endpoints) ? data.endpoints : [];
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error || this.$t("ncrm.workflows.errors.externalApiLoad");
      } finally {
        this.loadingExternalApi = false;
      }
    },
    async fetchAiPrompts() {
      this.loadingAiPrompts = true;
      this.aiPromptsError = "";
      try {
        const { data } = await axios.get("/ai/prompts");
        this.aiPrompts = Array.isArray(data?.items) ? data.items : [];
      } catch (error) {
        this.aiPrompts = [];
        this.aiPromptsError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          this.$t("ncrm.workflows.errors.aiPromptsLoad");
      } finally {
        this.loadingAiPrompts = false;
      }
    },
    async fetchDeliveriesForSelectedWorkflow() {
      if (!this.selectedWorkflow) {
        this.selectedWorkflowDeliveries = [];
        return;
      }

      const endpointId = resolveWorkflowApiEndpointId(this.selectedWorkflow, this.externalApiEndpoints);
      this.loadingDeliveries = true;
      try {
        const requests = [];
        if (this.selectedWorkflow.id) {
          requests.push(
            axios.get("/ncrm/integrations/external-api/deliveries", {
              params: {
                workflow_instance_id: this.selectedWorkflow.id,
                source_systems: PUBLIC_EXTERNAL_FORM_SOURCE_SYSTEMS.join(","),
              },
            })
          );
        }
        if (endpointId) {
          requests.push(
            axios.get("/ncrm/integrations/external-api/deliveries", {
              params: { endpointId },
            })
          );
        }

        const responses = await Promise.all(requests);
        const deliveriesById = new Map();
        responses.forEach((response) => {
          const deliveries = Array.isArray(response?.data?.deliveries) ? response.data.deliveries : [];
          deliveries.forEach((delivery) => {
            const key =
              delivery?.id ??
              `${delivery?.endpoint_id || ""}:${delivery?.source_system || ""}:${delivery?.received_at || ""}`;
            deliveriesById.set(key, delivery);
          });
        });

        this.selectedWorkflowDeliveries = Array.from(deliveriesById.values()).sort((a, b) => {
          const receivedAtA = new Date(a?.received_at || 0).getTime();
          const receivedAtB = new Date(b?.received_at || 0).getTime();
          if (receivedAtA !== receivedAtB) return receivedAtB - receivedAtA;
          return Number(b?.id || 0) - Number(a?.id || 0);
        });
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error || this.$t("ncrm.workflows.errors.deliveriesLoad");
      } finally {
        this.loadingDeliveries = false;
      }
    },
    goBackToList() {
      this.$router.push({ name: "NcrmWorkflowsHome" });
    },
    openEditWorkflow(workflow) {
      this.editingWorkflow = workflow;
      this.workflowDrawer = true;
    },
    async handleSaveWorkflow(payload) {
      if (!this.selectedWorkflow?.id) return;
      try {
        const { data } = await updateWorkflowInstanceApi(this.selectedWorkflow.id, payload);
        if (data?.workflow) this.persistWorkflowLocally(normalizeWorkflowInstance(data.workflow));
        this.workflowDrawer = false;
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.workflowSave");
      }
    },
    async handleDeleteWorkflow(workflow) {
      const workflowId = workflow?.id || this.selectedWorkflow?.id;
      if (!workflowId) return;
      try {
        await deleteWorkflowInstanceApi(workflowId);
        this.workflows = this.workflows.filter((item) => item.id !== workflowId);
        this.workflowDrawer = false;
        this.editingWorkflow = null;
        this.goBackToList();
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.workflowDelete");
      }
    },
    openNodeSettings(node) {
      this.editingNode = node;
      this.nodeSettingsDrawer = true;
    },
    async handleSaveNodeSettings(settings) {
      if (!this.selectedWorkflow || !this.editingNode?.key) return;
      try {
        const { data } = await updateWorkflowNodeSettingsApi(
          this.selectedWorkflow.id,
          this.editingNode.key,
          settings
        );
        if (data?.workflow) this.persistWorkflowLocally(normalizeWorkflowInstance(data.workflow));
        this.nodeSettingsDrawer = false;
        if (this.editingNode.key === WORKFLOW_NODE_KEYS.API_IMPORT) {
          await this.fetchDeliveriesForSelectedWorkflow();
          await this.fetchStagingRecords();
        }
        if (this.editingNode.key === WORKFLOW_NODE_KEYS.DATA_ENRICHMENT) {
          await this.fetchStagingRecords();
        }
        if ([WORKFLOW_NODE_KEYS.DUPLICATE_CHECK, WORKFLOW_NODE_KEYS.CRM_WRITE, WORKFLOW_NODE_KEYS.LEAD_BOARD_ADD].includes(this.editingNode.key)) {
          await this.fetchStagingRecords();
        }
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.nodeSettingsSave");
      }
    },
    async fetchStagingRecords() {
      if (!this.selectedWorkflow) { this.stagingRecords = []; return; }
      this.loadingStaging = true;
      try {
        const { data } = await fetchStagingRecords({
          workflow_instance_id: this.selectedWorkflow.id,
          limit: 5000,
        });
        this.stagingRecords = Array.isArray(data?.records) ? data.records : [];
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.stagingLoad");
      } finally {
        this.loadingStaging = false;
      }
    },
    async runStagingImport() {
      if (!this.selectedWorkflow) return;
      const endpointId = resolveWorkflowApiEndpointId(this.selectedWorkflow, this.externalApiEndpoints);
      if (!endpointId) {
        this.errorMessage = this.$t("ncrm.workflows.errors.noEndpointForImport");
        return;
      }
      this.loadingStagingImport = true;
      try {
        await importStagingFromEndpoint(endpointId, null, this.selectedWorkflow.id);
        await this.fetchStagingRecords();
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.stagingImport");
      } finally {
        this.loadingStagingImport = false;
      }
    },
    async runWorkflowDedup() {
      if (!this.selectedWorkflow?.id) return;
      this.loadingStagingDedup = true;
      try {
        await runDedupForWorkflow(this.selectedWorkflow.id);
        await this.fetchStagingRecords();
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.stagingDedup");
      } finally {
        this.loadingStagingDedup = false;
      }
    },
    openFileImportDialog() {
      if (!this.selectedWorkflow?.id) return;
      this.fileImportDialog = true;
    },
    async handleFileImported() {
      await this.fetchStagingRecords();
    },
    async handleRunEnrichmentBatch(payload = {}) {
      if (!this.selectedWorkflow?.id) return;
      const count = Number(payload.count || 0);
      if (!count) return;
      this.loadingStagingEnrichment = true;
      this.errorMessage = "";
      try {
        const { data } = await runEnrichmentForWorkflow(this.selectedWorkflow.id);
        this.infoMessage = this.$t("ncrm.workflows.enrichment.runQueuedBatch", {
          count: Number(data?.queued || data?.processed || 0),
        });
        await this.fetchStagingRecords();
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.stagingEnrichment");
      } finally {
        this.loadingStagingEnrichment = false;
      }
    },
    async handleRunEnrichmentRecord(record) {
      if (!this.selectedWorkflow?.id || !record?.id) return;
      this.loadingStagingEnrichment = true;
      this.errorMessage = "";
      try {
        const { data } = await runEnrichmentForStagingRecord(record.id, this.selectedWorkflow.id);
        const messageKey = data?.action === "synced"
          ? "ncrm.workflows.enrichment.runSyncedRow"
          : "ncrm.workflows.enrichment.runQueuedRow";
        this.infoMessage = this.$t(messageKey, {
          name: record?.name || record?.external_id || "-",
          status: data?.ai_status || "-",
        });
        await this.fetchStagingRecords();
        if (this.selectedStagingRecord?.id === record.id) {
          const { data: detailData } = await fetchStagingRecord(record.id, this.selectedWorkflow.id);
          if (detailData?.record) this.selectedStagingRecord = detailData.record;
        }
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.stagingEnrichment");
      } finally {
        this.loadingStagingEnrichment = false;
      }
    },
    async handleSyncEnrichment() {
      if (!this.selectedWorkflow?.id) return;
      this.loadingStagingEnrichmentSync = true;
      this.errorMessage = "";
      try {
        const { data } = await syncEnrichmentForWorkflow(this.selectedWorkflow.id);
        this.infoMessage = this.$t("ncrm.workflows.enrichment.syncDone", {
          succeeded: Number(data?.succeeded || 0),
          running: Number(data?.running || 0),
          failed: Number(data?.failed || 0),
        });
        await this.fetchStagingRecords();
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.stagingEnrichmentSync");
      } finally {
        this.loadingStagingEnrichmentSync = false;
      }
    },
    async openStagingRecord(record) {
      this.stagingDrawer = true;
      if (record?.id) {
        this.loadingStagingRecord = true;
        try {
          const workflowId = this.selectedWorkflow?.id;
          const { data } = await fetchStagingRecord(record.id, workflowId);
          this.selectedStagingRecord = data?.record || record;
        } catch (_) {
          this.selectedStagingRecord = record;
        } finally {
          this.loadingStagingRecord = false;
        }
      } else {
        this.selectedStagingRecord = record;
      }
    },
    async handleStagingApprove({ record, review_notes, matched_company_id }) {
      await this._updateStagingStatus(record, "approved", review_notes, { matched_company_id });
    },
    async handleStagingReject({ record, review_notes }) {
      await this._updateStagingStatus(record, "rejected", review_notes);
    },
    async _updateStagingStatus(record, status, review_notes, options = {}) {
      if (!record?.id) return;
      this.savingStagingRecord = true;
      try {
        await updateStagingStatus(record.id, status, review_notes, this.selectedWorkflow?.id, options);
        await this.fetchStagingRecords();
        const fresh = this.stagingRecords.find((r) => r.id === record.id);
        if (fresh) this.selectedStagingRecord = fresh;
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.stagingStatusUpdate");
      } finally {
        this.savingStagingRecord = false;
      }
    },
    async handleStagingDedup({ record }) {
      if (!record?.id) return;
      this.savingStagingRecord = true;
      try {
        await runDedupForStagingRecord(record.id, {
          workflow_instance_id: this.selectedWorkflow?.id,
        });
        await this.fetchStagingRecords();
        const { data } = await fetchStagingRecord(record.id, this.selectedWorkflow?.id);
        if (data?.record) this.selectedStagingRecord = data.record;
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.stagingDedup");
      } finally {
        this.savingStagingRecord = false;
      }
    },
    ...workflowDetailsWriteLeadActions,
    ...workflowDetailsMailjetActions,
    async handleStagingRemap({ record, field_updates, confirmed_unmapped }) {
      if (!record?.id) return;
      this.savingStagingRecord = true;
      try {
        await remapStagingFields(record.id, field_updates, confirmed_unmapped, true, this.selectedWorkflow?.id);
        const { data } = await fetchStagingRecord(record.id, this.selectedWorkflow?.id);
        if (data?.record) this.selectedStagingRecord = data.record;
        await this.fetchStagingRecords();
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("ncrm.workflows.errors.stagingRemap");
      } finally {
        this.savingStagingRecord = false;
      }
    },
    async handleStagingEnrichmentReview({ record, decisions }) {
      if (!record?.id || !this.selectedWorkflow?.id) return;
      this.savingStagingRecord = true;
      try {
        const { data } = await reviewEnrichmentSuggestions(
          record.id,
          this.selectedWorkflow.id,
          decisions
        );
        if (data?.record) this.selectedStagingRecord = data.record;
        await this.fetchStagingRecords();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error || this.$t("ncrm.workflows.errors.stagingEnrichmentReview");
      } finally {
        this.savingStagingRecord = false;
      }
    },
    async handleStagingEnrichmentDedupRecheck({ record }) {
      if (!record?.id || !this.selectedWorkflow?.id) return;
      this.savingStagingRecord = true;
      this.errorMessage = "";
      try {
        const { data } = await recheckEnrichmentDedupForStagingRecord(
          record.id,
          this.selectedWorkflow.id
        );
        if (data?.record) this.selectedStagingRecord = data.record;
        this.infoMessage = this.$t("ncrm.workflows.enrichment.dedupRecheckDone", {
          score: data?.post_enrichment_dedup?.dedup_score ?? "-",
          candidates: data?.post_enrichment_dedup?.candidates_count ?? 0,
        });
        await this.fetchStagingRecords();
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error || this.$t("ncrm.workflows.errors.stagingDedup");
      } finally {
        this.savingStagingRecord = false;
      }
    },
    openExternalApiSettings() {
      this.$router.push({ path: "/ncrm/data-integrations", query: { tab: "external" } });
    },
    openAiWorkspacePrompts() {
      this.$router.push({ path: "/ai-workspace", query: { tab: "prompts" } });
    },
    openDeliveryInExternalApi(deliveryId) {
      if (!deliveryId) return;
      this.$router.push({
        path: "/ncrm/data-integrations",
        query: { tab: "external", deliveryId: String(deliveryId) },
      });
    },
  },
};
</script>
<style src="./NcrmWorkflowDetailsPage.css" scoped></style>
